import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme modes
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// Theme context
const ThemeContext = createContext({
  theme: THEME_MODES.SYSTEM,
  resolvedTheme: THEME_MODES.LIGHT,
  setTheme: () => {},
  toggleTheme: () => {},
  systemPreference: THEME_MODES.LIGHT
});

// Custom hook for using theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
export const ThemeProvider = ({ children, defaultTheme = THEME_MODES.SYSTEM }) => {
  const [theme, setTheme] = useState(() => {
    // Get stored theme preference or use default
    const storedTheme = localStorage.getItem('wixell-theme');
    return storedTheme || defaultTheme;
  });

  const [systemPreference, setSystemPreference] = useState(() => {
    // Check if browser supports matchMedia
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? THEME_MODES.DARK
        : THEME_MODES.LIGHT;
    }
    return THEME_MODES.LIGHT;
  });

  // Resolved theme (actual theme being used)
  const resolvedTheme = theme === THEME_MODES.SYSTEM ? systemPreference : theme;

  // Update theme in localStorage and DOM
  useEffect(() => {
    localStorage.setItem('wixell-theme', theme);

    // Update document root class for CSS variables
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);

    // Update theme color meta tag for browser UI
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.content = resolvedTheme === THEME_MODES.DARK ? '#1a1a1a' : '#ffffff';
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = resolvedTheme === THEME_MODES.DARK ? '#1a1a1a' : '#ffffff';
      document.head.appendChild(meta);
    }
  }, [theme, resolvedTheme]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setSystemPreference(e.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Toggle between light and dark (ignoring system)
  const toggleTheme = () => {
    setTheme(current => {
      if (current === THEME_MODES.SYSTEM) {
        // If on system, switch to opposite of current system preference
        return systemPreference === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK;
      }
      // Toggle between light and dark
      return current === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK;
    });
  };

  const value = {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    systemPreference
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Theme utilities
export const getThemeColors = (theme) => {
  const colors = {
    light: {
      background: '#ffffff',
      foreground: '#000000',
      card: '#f8f9fa',
      cardForeground: '#000000',
      primary: '#3b82f6',
      primaryForeground: '#ffffff',
      secondary: '#e5e7eb',
      secondaryForeground: '#000000',
      muted: '#f3f4f6',
      mutedForeground: '#6b7280',
      accent: '#f3f4f6',
      accentForeground: '#000000',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      border: '#e5e7eb',
      input: '#e5e7eb',
      ring: '#3b82f6',
    },
    dark: {
      background: '#0a0a0a',
      foreground: '#fafafa',
      card: '#1a1a1a',
      cardForeground: '#fafafa',
      primary: '#60a5fa',
      primaryForeground: '#000000',
      secondary: '#262626',
      secondaryForeground: '#fafafa',
      muted: '#262626',
      mutedForeground: '#a1a1aa',
      accent: '#262626',
      accentForeground: '#fafafa',
      destructive: '#991b1b',
      destructiveForeground: '#fafafa',
      border: '#262626',
      input: '#262626',
      ring: '#60a5fa',
    }
  };

  return colors[theme] || colors.light;
};

// CSS variable updater
export const updateCSSVariables = (theme) => {
  const colors = getThemeColors(theme);
  const root = document.documentElement;

  Object.entries(colors).forEach(([key, value]) => {
    // Convert camelCase to kebab-case
    const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });
};

export default ThemeContext;
